import { faAngleDown } from '@awesome.me/kit-5de77b2c02/icons/classic/solid'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import cx from 'classnames'
import { KeyboardEvent, useCallback, useId, useRef, useState } from 'react'
import { useDropDownMenu } from '../../helpers/hooks/useDropDownMenu'
import Checkbox from '../Checkbox'
import listStyles from '../DropdownList/DropdownList.module.scss'
import ExpandableComponent from '../ExpandableComponent/ExpandableComponent'
import ExpandableSectionTitle from '../ExpandableComponent/ExpandableSectionTitle'
import { ItemGroup } from '../GenericDropdownMenu/types'

import styles from '../GenericDropdownMenu/GenericDropdownMenu.module.scss'

const ACTIVATION_KEYS = ['Enter', ' ']

/*
  Single-select grouped dropdown built from the shared dropdown primitives
  (ExpandableComponent + Checkbox), generic over any grouped items. The group
  holding the value auto-opens to its checked child, and only one group is
  expanded at a time.
*/
interface Props {
  groups: readonly ItemGroup[]
  value?: string
  // undefined when the current selection is cleared (clicking the checked child)
  onChange: (value: string | undefined) => void
  /* Fixed pill text (e.g. "Rating"); falls back to the selected value. */
  label?: string
  placeholder?: string
  openRight?: boolean
  testId?: string
}

const GroupedSingleSelect = ({
  groups,
  value,
  onChange,
  label,
  placeholder = 'Select',
  openRight = false,
  testId = 'grouped-single-select-trigger'
}: Props) => {
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const menuId = useId()
  const { displayMenu, setDisplayMenu } = useDropDownMenu(ref)

  // The single open group (accordion). Seed it with the group holding the
  // current value so a selection is revealed the moment the menu opens.
  const [expandedGroup, setExpandedGroup] = useState<string | undefined>(
    () => groups.find((group) => group.values.includes(value ?? ''))?.label
  )

  const toggleMenu = useCallback(
    () => setDisplayMenu((open: boolean) => !open),
    [setDisplayMenu]
  )

  const closeMenu = useCallback(() => {
    setDisplayMenu(false)
    triggerRef.current?.focus()
  }, [setDisplayMenu])

  const pick = useCallback(
    (option: string) => () => {
      onChange(option === value ? undefined : option)
      closeMenu()
    },
    [onChange, value, closeMenu]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape' && displayMenu) {
        closeMenu()
      }
    },
    [displayMenu, closeMenu]
  )

  const handleTriggerKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (ACTIVATION_KEYS.includes(event.key)) {
        event.preventDefault()
        toggleMenu()
      }
    },
    [toggleMenu]
  )

  const makeSetExpanded = useCallback(
    (groupLabel: string) => (next: boolean) =>
      setExpandedGroup(next ? groupLabel : undefined),
    []
  )

  const makeGroupKeyDown = useCallback(
    (groupLabel: string, expanded: boolean) =>
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (ACTIVATION_KEYS.includes(event.key)) {
          event.preventDefault()
          setExpandedGroup(expanded ? undefined : groupLabel)
        }
      },
    []
  )

  return (
    <div
      ref={ref}
      className={styles.dropdownContainer}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={triggerRef}
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        aria-expanded={displayMenu}
        aria-controls={menuId}
        className={styles.dropdown}
        onClick={toggleMenu}
        onKeyDown={handleTriggerKeyDown}
        data-testid={testId}
      >
        <span className={styles.dropdownValue}>
          {label ?? value ?? placeholder}
        </span>
        <FontAwesomeIcon icon={faAngleDown} />
      </div>
      {displayMenu && (
        <div
          id={menuId}
          className={cx(styles.menu, { [styles.menuRight]: openRight })}
        >
          <div className={listStyles.dropdownMenu}>
            {groups.map((group) => {
              const isExpanded = expandedGroup === group.label
              const groupId = `${menuId}-${group.label}`
              return (
                <ExpandableComponent
                  // ExpandableComponent is uncontrolled, so remount on toggle
                  // to drive expansion from the lifted accordion state.
                  key={`${group.label}-${isExpanded}`}
                  isInitiallyExpanded={isExpanded}
                  title={(expanded) => (
                    <ExpandableSectionTitle
                      className={styles.groupTitle}
                      isExpanded={expanded}
                      setExpanded={makeSetExpanded(group.label)}
                      role="button"
                      tabIndex={0}
                      aria-expanded={expanded}
                      aria-controls={groupId}
                      onKeyDown={makeGroupKeyDown(group.label, expanded)}
                      data-testid={`group-${group.label}`}
                    >
                      {group.label}
                    </ExpandableSectionTitle>
                  )}
                >
                  <div id={groupId} role="group" aria-label={group.label}>
                    {group.values.map((option) => (
                      <div key={option} className={styles.singleOption}>
                        <Checkbox
                          locator={`option-${option}`}
                          checked={option === value}
                          onChange={pick(option)}
                        >
                          {option}
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                </ExpandableComponent>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default GroupedSingleSelect
